import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { RASHI_NAMES } from '@astrally/shared';
import type { AstroSystem, Signal } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';
import { RATE_OPTIONS, type RateVerdict } from './accuracy';
import { areaLabel } from './compare';
import { SYSTEM_LABELS, type ComputedReading, type ReadingOutcome } from './horoscopeData';
import {
  ENGINE_ERROR_HINT,
  MORE_SIGNALS,
  NO_CHART_CTA,
  RATE_ARIA,
  RATE_HELP,
  RATE_PROMPT,
  SEE_WESTERN,
  SHOW_LESS,
  SHOW_MORE,
} from './copy';

/**
 * One tradition's reading, and the rate control that turns it into data.
 *
 * Everything drawn here is DETERMINISTIC. `buildReading` computed it on this
 * device out of `@astrally/astro-engine`, so the card renders on the first
 * frame, offline, at zero cost, and reopening the screen fifty times costs
 * nothing. No AI call may ever be started from this component.
 *
 * The card wears `data-mode`, which is what remaps `--mode-*` to the warm
 * Jyotish or cool Western palette (see the tradition-mode block in
 * global.css). Any colour below therefore reads correctly in both traditions
 * and in both themes without this file knowing which it is in.
 */

/* --- rating ---------------------------------------------------------------- */

/**
 * Colour is never the only signal: each verdict also carries its own glyph and
 * its own word, so the control is legible in greyscale and to anyone who does
 * not separate red from green.
 */
const VERDICT_GLYPH: Record<RateVerdict, string> = {
  hit: '✓',
  partial: '≈',
  miss: '✕',
};

const VERDICT_ON: Record<RateVerdict, CSSProperties> = {
  hit: {
    background: 'var(--ok-soft)',
    borderColor: 'var(--ok)',
    color: 'var(--ok)',
    fontWeight: 600,
  },
  partial: {
    background: 'var(--gold-soft)',
    borderColor: 'var(--gold-line)',
    color: 'var(--gold-ink)',
    fontWeight: 600,
  },
  miss: {
    background: 'var(--danger-soft)',
    borderColor: 'var(--danger-line)',
    color: 'var(--danger)',
    fontWeight: 600,
  },
};

export interface RatingBridge {
  verdictFor(readingKey: string, signalId: string): RateVerdict | undefined;
  /** Must return synchronously — see the one-tap contract in accuracy.ts. */
  onRate(reading: ComputedReading, signal: Signal, verdict: RateVerdict): void;
}

/**
 * Three buttons, one tap, no network in the path.
 *
 * `accuracy.ts` writes the verdict into React state before it returns and only
 * then syncs in the background, so this never shows a spinner and never
 * disables itself. Tapping the verdict already showing clears it, which is the
 * only undo a mis-tap needs.
 */
function RateControl({
  reading,
  signal,
  rating,
}: {
  reading: ComputedReading;
  signal: Signal;
  rating: RatingBridge;
}) {
  const current = rating.verdictFor(reading.readingKey, signal.id);
  return (
    <div
      className="wrap"
      role="group"
      aria-label={`${RATE_ARIA}: ${signal.label}`}
      style={{ gap: 6, marginTop: 8 }}
    >
      <span className="tiny faint" style={{ alignSelf: 'center', marginRight: 2 }}>
        {RATE_PROMPT}
      </span>
      {RATE_OPTIONS.map((option) => {
        const on = current === option.verdict;
        return (
          <button
            key={option.verdict}
            type="button"
            className="chip"
            aria-pressed={on}
            title={option.hint}
            style={{ padding: '4px 12px', ...(on ? VERDICT_ON[option.verdict] : {}) }}
            onClick={() => rating.onRate(reading, signal, option.verdict)}
          >
            <span aria-hidden>{VERDICT_GLYPH[option.verdict]}</span>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* --- one signal ------------------------------------------------------------ */

function SignalRow({
  reading,
  signal,
  rating,
}: {
  reading: ComputedReading;
  signal: Signal;
  rating: RatingBridge | null;
}) {
  return (
    <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)' }}>
      <div className="spread" style={{ alignItems: 'baseline', gap: 8 }}>
        <span
          className="small"
          style={{ color: 'var(--mode-ink)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          {signal.label}
          {signal.term && <InfoDot term={signal.term} />}
        </span>
        <span className="tiny faint" style={{ flexShrink: 0 }}>
          {areaLabel(signal.area)}
        </span>
      </div>
      <div className="tiny muted" style={{ lineHeight: 1.55, marginTop: 3 }}>
        {signal.meaning}
      </div>
      {rating && <RateControl reading={reading} signal={signal} rating={rating} />}
    </div>
  );
}

/* --- the card -------------------------------------------------------------- */

export interface ReadingCardProps {
  system: AstroSystem;
  outcome: ReadingOutcome;
  /** Null when there is nothing to rate against — no chart, or no birth profile. */
  rating: RatingBridge | null;
  /** Human label for the window, e.g. "Thursday, 7 August". */
  periodLabel: string;
  /**
   * Offered when a Jyotish reading is refused for an uncertain Moon and the
   * Western reading is not already on screen. Omitted in the "Both" view,
   * where it is sitting right there.
   */
  onShowWestern?: () => void;
}

export function ReadingCard({
  system,
  outcome,
  rating,
  periodLabel,
  onShowWestern,
}: ReadingCardProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const label = SYSTEM_LABELS[system];

  /**
   * Everything the engine retained that the `strongest` list did not already
   * show, heaviest first. `byArea` holds the full set; `strongest` is a slice
   * of the same objects, so identity is enough to subtract them.
   */
  const rest = useMemo(() => {
    if (!outcome.ok) return [];
    const shown = new Set(outcome.reading.result.strongest.map((s) => s.id));
    return Object.values(outcome.reading.result.byArea)
      .flat()
      .filter((s) => !shown.has(s.id))
      .sort((a, b) => b.weight - a.weight);
  }, [outcome]);

  return (
    <div className="card" data-mode={system}>
      <div className="spread" style={{ marginBottom: 8, alignItems: 'flex-start', gap: 8 }}>
        <span className="mode-chip">{label.name}</span>
        <span className="tiny faint" style={{ textAlign: 'right' }}>
          {periodLabel}
        </span>
      </div>
      <div className="mode-rule" style={{ marginBottom: 10 }} />

      {!outcome.ok ? (
        <Failure system={system} outcome={outcome} onShowWestern={onShowWestern} />
      ) : (
        <>
          {/* THE RASHI IS THE HEADLINE OF THE READER'S DAY, so it renders as
              the card's largest element — named, tappable, theirs. This is
              what "where are the Rashis — Dhanu, etc" was asking for: every
              rashifal in every Indian paper leads with the rashi name, and a
              reading that never says "Kanya" reads like it was written for
              nobody. Western has no rashi to lead with and keeps its line. */}
          {system === 'vedic' && (
            <div className="h2" style={{ marginBottom: 6 }}>
              <Term id={`rashi.${RASHI_NAMES[outcome.reading.result.anchorRashi].toLowerCase()}`}>
                {RASHI_NAMES[outcome.reading.result.anchorRashi]}
              </Term>{' '}
              <span className="muted" style={{ fontWeight: 400 }}>
                rashi
              </span>
            </div>
          )}
          <div className="small" style={{ lineHeight: 1.55, marginBottom: 4 }}>
            {outcome.reading.result.headline}
          </div>
          <div className="tiny faint" style={{ lineHeight: 1.5, marginBottom: 10 }}>
            {/* Same words as SYSTEM_LABELS.vedic.anchor, rebuilt as JSX so the
                one Indian term in it is tappable. Western keeps its own line. */}
            {system === 'vedic' ? (
              <>
                Counted from your <Term id="moon-sign">Chandra rashi</Term> — the rashi your Moon
                held when you were born. {label.zodiac}.
              </>
            ) : (
              <>
                {label.anchor} {label.zodiac}.
              </>
            )}
          </div>

          {outcome.reading.result.strongest.length === 0 ? (
            <div
              className="tiny muted"
              style={{ lineHeight: 1.55, paddingTop: 10, borderTop: '1px solid var(--border)' }}
            >
              {t('horoscope.quietDay')}
            </div>
          ) : (
            <div className="col" style={{ gap: 12 }}>
              {outcome.reading.result.strongest.map((signal) => (
                <SignalRow
                  key={signal.id}
                  reading={outcome.reading}
                  signal={signal}
                  rating={rating}
                />
              ))}
            </div>
          )}

          {rating && outcome.reading.result.strongest.length > 0 && (
            <div className="tiny faint" style={{ lineHeight: 1.5, marginTop: 10 }}>
              {RATE_HELP}
            </div>
          )}

          {rest.length > 0 && (
            <>
              <div className="spread" style={{ marginTop: 14 }}>
                <span className="tiny faint">{MORE_SIGNALS}</span>
                <button
                  className="btn btn--ghost btn--small"
                  aria-expanded={expanded}
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? SHOW_LESS : `${SHOW_MORE} (${rest.length})`}
                </button>
              </div>
              {expanded && (
                <div className="col" style={{ gap: 12, marginTop: 8 }}>
                  {rest.map((signal) => (
                    <SignalRow
                      key={signal.id}
                      reading={outcome.reading}
                      signal={signal}
                      rating={rating}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {outcome.reading.result.sadeSati && (
            <div className="tiny faint" style={{ marginTop: 12, lineHeight: 1.5 }}>
              {t('horoscope.sadeSati')}
              <InfoDot term="sade-sati" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The three ways a reading legitimately does not exist.
 *
 * The important one is `moon-sign-uncertain`: a Jyotish period reading is
 * counted in signs from the natal Moon, so a Moon that could be in either of
 * two signs does not give a slightly worse reading — it gives a different one.
 * Astrally refuses rather than flipping a coin, and says so in the same words
 * Home uses, from the same i18n key, so the two screens can never drift.
 *
 * That key is written for the Jyotish side and ends by pointing at the Western
 * reading, so it is only used there. Western reaches this branch by a
 * different route — no natal Moon at all, in either zodiac — and gets the data
 * layer's own message instead, which is the true one for that case.
 */
function Failure({
  system,
  outcome,
  onShowWestern,
}: {
  system: AstroSystem;
  outcome: ReadingOutcome;
  onShowWestern?: () => void;
}) {
  const { t } = useI18n();
  if (outcome.ok) return null;

  if (outcome.reason === 'no-chart') {
    return (
      <>
        <div className="small muted" style={{ lineHeight: 1.55, marginBottom: 10 }}>
          {t('home.calculating')}
        </div>
        <Link to="/profile">
          <button className="btn btn--ghost btn--small btn--block">{NO_CHART_CTA} →</button>
        </Link>
      </>
    );
  }

  if (outcome.reason === 'moon-sign-uncertain') {
    return (
      <>
        <div className="small muted" style={{ lineHeight: 1.55, marginBottom: 10 }}>
          {system === 'vedic' ? t('home.moonUncertain') : outcome.message}
        </div>
        <Link to="/profile/refine">
          <button className="btn btn--ghost btn--small btn--block">
            {t('chart.refine.cta')} →
          </button>
        </Link>
        {system === 'vedic' && onShowWestern && (
          <button
            className="btn btn--ghost btn--small btn--block"
            style={{ marginTop: 8 }}
            onClick={onShowWestern}
          >
            {SEE_WESTERN} →
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <div className="small muted" style={{ lineHeight: 1.55 }}>
        {outcome.message}
      </div>
      <div className="tiny faint" style={{ lineHeight: 1.5, marginTop: 6 }}>
        {ENGINE_ERROR_HINT}
      </div>
    </>
  );
}
